/**
 * @file: 工具函数库
 * @author: xiaolong.huang@dmall.com
 */

/**
 * 深拷贝
 *
 * @param {Object} originObj
 * @return {Object}
 */
export const deepCopy = (originObj: any): any => {
    if (originObj instanceof Array) {
        let n: any[] = [];

        for (let i = 0; i < originObj.length; ++i) {
            n[i] = deepCopy(originObj[i]);
        }
        return n;
    }
    else if (originObj instanceof Function) {
        let n:any = new Function("return " + originObj.toString())();

        return n;
    }
    else if (originObj instanceof Object) {
        let n:any = {};

        for (let i in originObj) {
            n[i] = deepCopy(originObj[i]);
        }
        return n;
    }
    else {
        return originObj;
    }
}

export const mix = (r: any, s: any): void => {
    for (let p in s) {
        r[p] = s[p];
    }
}

/**
 * url参数解析
 *
 * @param {Number} paramStr url参数
 * @return {Object}
 */
export function queryUrl(url: string): FreeObjMap {
    let paramStr: string = url.split('?')[1];
    let result: FreeObjMap = {};

    if (!paramStr) {
        return result;
    }

    let paramList: Array<string> = paramStr.split('&');

    paramList.forEach((param) => {
        let key: string = param.split('=')[0];
        let value: string = param.split('=')[1];

        result[key] = value;
    });

    return result;
}

/**
 * 通过字符串key取map值
 *
 * @param {key} String 'key.key.key'
 */
export function getMapValueByStringKey(target: FreeObjMap, key: string): any {
    // 判断key是否合法
    if (!isExist(key)) {
        return null;
    }
    let keys: string[] = key.split('.');
    let result: any = target;

    while (keys.length) {
        let keyNow: string = keys.shift();

        if (keyNow === '') {
            return null;
        }

        let arrayIndex: number = NaN;

        // 是数组，取索引和字段，这里只考虑一维数组
        if (/\[/.test(keyNow)) {
            let keySplit: string[] = keyNow.split('[');

            arrayIndex = parseInt(keySplit[1].match(/\d+/)[0], 10);
            keyNow = keySplit[0];
        }

        if (typeof result[keyNow] !== 'undefined') {
            result = result[keyNow];
            if (!isNaN(arrayIndex)) {
                result = result[arrayIndex];
            }
        }
        else {
            result = undefined;
            break;
        }
    }

    return result;
}

/**
 * 通过字符串key取map值
 *
 * @param {key} String key字符串格式'key.key.key'
 * @return {void}
 */
export function setMapValueByStringKey(target: FreeObjMap, key: string, value: any): any {
    let keyArr: string[] = key.split('.');
    if (!keyArr.length) {
        return null;
    }
    let targetKey: string = keyArr.pop();
    let parentKey: string = keyArr.join('.');
    let keyObj: FreeObjMap;
    let arrayIndex: number = NaN;

    // 是数组，取索引和字段，这里只考虑一维数组
    if (/\[/.test(targetKey)) {
        let keySplit: string[] = targetKey.split('[');

        arrayIndex = parseInt(keySplit[1].match(/\d+/)[0], 10);
        parentKey = parentKey + '.' + keySplit[0];
    }

    // 查找最后一个key的父级
    if (parentKey === '') {
        keyObj = target;
    }
    else {
        keyObj = getMapValueByStringKey(target, parentKey);
    }

    // 如果父级不存在，则放弃set操作, 避免报错
    if (keyObj === undefined || keyObj === null) {
        return null;
    }

    if (!isNaN(arrayIndex)) {
        keyObj[arrayIndex] = value;
    }
    else {
        keyObj[targetKey] = value;
    }

    return true;
}

// 判断是否有值
export function isExist(target: any): boolean {
    return target !== null && target !== undefined;
}

// 判断是否是合法数值
export function isValidateNumber(target: any): boolean {
    return isExist(target) && !isNaN(target) && typeof target === 'number';
}

// 判断是有值的字符串
export function isNotEmptyString(target: any): boolean {
    return isExist(target) && typeof target === 'string' && target !== '';
}

// 判断对象下是否存在指定key
export function hasKey(target: FreeObjMap, key: string): boolean {
    return key in target;
}

export function hasValue(target: FreeObjMap, value: any): boolean {
    for (let key in target) {
        if (target[key] === value) {
            return true;
        }
    }

    return false;
}

// 转number类型
export function toNumber(val: any): number {
    return parseFloat(val);
}

// 断言
export function assert(expression): boolean {
    if (!expression) {
        console.warn('断言不成立');
    }

    return expression;
}

// 组合函数
export function createComposeFunc(...fns) {
    return function composed(...args){
        // 拷贝一份保存函数的数组
        let result = args;

        for (let i = fns.length - 1; i >= 0; i--) {
            args = [fns[i](...args)];
        }

        return args[0];
    };
}

// 根据字段快排
export function quickSortByKey(arr: Array<FreeObjMap>, key: string): FreeObjMap[] {
    if (Object.prototype.toString.call(arr) !== '[object Array]') {
        return [];
    }
    if (arr.length < 1) {
        return arr;
    }
    var pivotIndex = Math.floor(arr.length / 2);
    var pivot = arr.splice(pivotIndex, 1)[0];
    var left = [];
    var right = [];

    for (var i = 0; i < arr.length; i++) {
        let cur: any = getMapValueByStringKey(arr[i], key);
        let target: any = getMapValueByStringKey(pivot, key);

        if (cur < target) {
            left.push(arr[i]);
        }

        if (cur === target && i <= pivotIndex - 1) {
            left.push(arr[i]);
        }

        if (cur > target) {
            right.push(arr[i]);
        }

        if (cur === target && i > pivotIndex - 1) {
            right.push(arr[i]);
        }
    }

    return quickSortByKey(left, key).concat([pivot], quickSortByKey(right, key));
}

// 快排
export function quickSort(arr: Array<number>): number[] {
    if (Object.prototype.toString.call(arr) !== '[object Array]') {
        return [];
    }
    if (arr.length <= 1) {
        return arr;
    }
    var pivotIndex = Math.floor(arr.length / 2);
    var pivot = arr.splice(pivotIndex, 1)[0];
    var left = [];
    var right = [];
    for (var i = 0; i < arr.length; i++) {
        if (arr[i] < pivot) {
            left.push(arr[i]);
        } else {
            right.push(arr[i]);
        }
    }

    return quickSort(left).concat([pivot], quickSort(right));
}

// 去重
export function uniqueString<T>(arr: T[]): T[] {
    return Array.from(new Set(arr));
}

// 数组类型判断
export function isArray(value: any): boolean {
    return Array.isArray(value);
}

// 对象类型判断
export function isObject(value: any): boolean {
    return Object.prototype.toString.call(value) === '[object Object]';
}

export function correctNaN(num: number): number {
    return isNaN(num) ? 0 : num;
}

export function toFixed(num: any, len: number): string {
    let numTemp: number = toNumberForce(num);
    let numStr: string = num + '';
    let numArr: Array<string> = numStr.split('.');
    let isFloat: boolean = numArr.length > 1;
    let result: string;

    if (!isFloat) {
        // 整数
        let zeroStr: string = len > 0 ? '.' : '';

        for (let i = 0; i < len; i++) {
            zeroStr += '0';
        }

        result = `${numStr}${zeroStr}`;
    }
    else {
        // 浮点数
        let floatStr: string = numArr[1];
        let newfloatStr: string = len > 0 ? '.' : '';

        for (let i = 0; i < len; i++) {
            if (floatStr.charAt(i) !== '') {
                newfloatStr += floatStr.charAt(i);
            }
            else {
                newfloatStr += '0';
            }
        }

        result = `${numArr[0]}${newfloatStr}`;
    }

    return result;
}

export function unique(list: Array<any>): Array<any> {
    return Array.from(new Set(list));
}

export const toNumberForce: (num: any) => number = createComposeFunc(correctNaN, toNumber);
export const toFixedNumber: (num: number, len: number) => number = createComposeFunc(correctNaN, toNumber, toFixed);

// 展开二维数组
export function expandArray(lists: Array<Array<any>>): Array<any> {
    let result: Array<any> = [];

    lists.forEach((list: Array<any>): void => {
        list.forEach((value: any): void => {
            result.push(value);
        });
    });

    return result;
}

function isInteger(num: number): boolean {
    return Math.floor(num) === num;
}

interface Integer {
    times: number,
    num: number
};
function toInteger(floatNum: string|number): Integer {
    let isNegative: boolean = false;
    let floatNumStr: string = floatNum + '';

    if (floatNumStr[0] === '-') {
        isNegative = true;
        floatNum = floatNumStr.split('-')[1];
    }

    let ret: Integer = {
        times: 1,
        num: 0
    };

    if (isInteger(toNumber(floatNum))) {
        ret.num = toNumber(floatNum);

        if (isNegative) {
            ret.num = 0 - ret.num;
        }

        return ret;
    }

    let strfi: string = floatNum + '';
    let dotPos: number = strfi.indexOf('.');
    let len: number = strfi.substr(dotPos + 1).length;
    let time: number = Math.pow(10, len);
    let sum: string = (toNumber(floatNum) * time + 0.5) + '';
    let intNum: number = parseInt(sum, 10);

    if (isNegative) {
        intNum = 0 - intNum;
    }

    ret.times = time;
    ret.num = intNum;

    return ret;
}

/**
 * 浮点数运算
 */
type OperationType = '+' | '-' | '*' | '/';
export function math(a: string|number, b: string|number, op: OperationType): number {
    let o1: Integer = toInteger(a);
    let o2: Integer = toInteger(b);
    let n1: number = o1.num;
    let n2: number = o2.num;
    let t1: number = o1.times;
    let t2: number = o2.times;
    let max: number = t1 > t2 ? t1 : t2;
    let result: number;

    switch (op) {
        // 加法
        case '+':
            if (t1 === t2) {
                result = n1 + n2;
            }
            else if (t1 > t2) {
                result = n1 + n2 * (t1 / t2);
            }
            else {
                result = n1 * (t2 / t1) + n2;
            }

            return result / max;
            break;

        // 减法
        case '-':
            if (t1 === t2) {
                result = n1 - n2;
            }
            else if (t1 > t2) {
                result = n1 - n2 * (t1 / t2);
            }
            else {
                result = n1 * (t2 / t1) - n2;
            }

            return result / max;
            break;

        // 乘法
        case '*':
            result = (n1 * n2) / (t1 * t2);

            return result;
            break;

        // 除法
        case '/':
            result = (n1 / n2) / (t1 / t2);

            return result;
            break;
    }
}

// 防抖函数
export function debounce(func: (...args: any[]) => any, wait: number): (...args: any[]) => void {
    let timer: number;

    return function(...args: any[]): void {
        if (timer) clearTimeout(timer);

        timer = setTimeout(() => {
            func.apply(this, args)
        }, wait);
    }
}

// 节流函数
export function throttle(func: (...args: any[]) => any, wait: number): (...args: any[]) => void {
    let prev: number = Date.now();

    return function(...args: any[]): void {
        let now: number = Date.now();

        if (now - prev >= wait) {
            func.apply(this, args);
            prev = Date.now();
        }
    }
}

// 数组元素交换位置
export function listExchange(list: Array<any>, startIndex: number, endIndex: number): void {
    if (startIndex > list.length - 1
            || startIndex < 0
            || endIndex > list.length - 1
            || endIndex < 0) {
        // 索引越界
        return;
    }
    list.splice(startIndex, 1, ...list.splice(endIndex, 1, list[startIndex]));
}

export function getDate(): string {
    let date: Date = new Date();
    let year: number = date.getFullYear();
    let month: string = toDouble(date.getMonth() + 1);
    let day: string = toDouble(date.getDate());
    let hours: string = toDouble(date.getHours());
    let min: string = toDouble(date.getMinutes());
    let sec: string = toDouble(date.getSeconds());
    let millSec: number = date.getMilliseconds();

    return `${year}-${month}-${day} ${hours}:${min}:${sec} ${millSec}ms`;
}

export function toDouble(num: number): string {
    if (num < 10) {
        return '0' + num;
    } else {
        return num + '';
    }
}

// 求数组交集
export function arrMixValue(arr1: any[], arr2: any[]): any[] {
    let result: Set<any> = new Set();
    let arr1List: Set<any> = new Set(arr1);

    arr2.forEach((val: any): void => {
        if (arr1List.has(val)) {
            result.add(val);
        }
    });

    return Array.from(result);
}

// 求数组并集
export function arrAddition(arr1: any[], arr2: any[]): any[] {
    let result: Set<any> = new Set(arr1);

    arr2.forEach((val: any): void => {
        result.add(val);
    });

    return Array.from(result);
}

export function isEmptyObj(target: FreeObjMap): boolean {
    return Object.keys(target).length === 0;
}

export function getSameItemFromList(srcList: FreeObjMap[], query: FreeObjMap): any[] {
    let result: any[] = [];

    srcList.forEach((src: FreeObjMap): void => {
        if (objIsBeContain(src, query)) {
            result.push(deepCopy(src));
        }
    });

    return result;
}

function objIsBeContain(src: FreeObjMap, target: FreeObjMap): boolean {
    for (let key in target) {
        if (src[key] !== target[key]) {
            return false;
        }
    }

    return true;
}

export function findListFirstItem(list: any[], skip: any[] = []): any {
    let skipList: Set<any> = new Set(skip);

    for (let i = 0; i < list.length; i++) {
        if (!skipList.has(list[i])) {
            return list[i];
        }
    }

    return null;
}

export function findListLastItem(list: any[], skip: any[] = []): any {
    let skipList: Set<any> = new Set(skip);

    for (let i = list.length - 1; i >= 0; i--) {
        if (!skipList.has(list[i])) {
            return list[i];
        }
    }

    return null;
}

// 数字按比例缩小
export function numberZoomIn(num: number, proportion: number): number {
    let dis: number = math(num, proportion, '*');

    return math(num, dis, '-');
}

// 数字按比例放大
export function numberZoomOut(num: number, proportion: number): number {
    let p: number = math(1, proportion, '-');

    return math(num, p, '/');
}

export function createDeepReader(target: any): any {
    return new Proxy(target, {
        get(target: any, key: any) {
            let value: any = Reflect.get(target, key);

            if (!isObject(value) && !isArray(value)) {
                return value;
            }

            return createDeepReader(value);
        },

        set(target: any, key: string, value: any): boolean {
            return true;
        }
    });
}

// 是否是json字符串
export function isJsonString(val: any): boolean {
    let result: boolean = true;
    let re: RegExp = /^{.*}$/;

    if (typeof val !== 'string') {
        return false;
    }

    if (!re.test(val)) {
        return false;
    }

    try {
        JSON.parse(val);
        result = true;
    }
    catch(err) {
        result = false;
    }

    return result;
}

export function getTargetValueKeyName(target: FreeObjMap, val: any): string {
    for (let key in target) {
        if (target[key] === val) {
            return key;
        }
    }

    return '';
}

export function listDeleteItems(list: Array<any>, deleteItems: Array<any>): Array<any> {
    let listSet: Set<any> = new Set(list);

    deleteItems.forEach((item: any): void => {
        if (listSet.has(item)) {
            listSet.delete(item);
        }
    });

    return Array.from(listSet);
}

export function getImgHost(isRetrunWMHost: boolean): string {
    var hosts = "https://img.dmallcdn.com/"
    if (isRetrunWMHost) {
        hosts = ""
    }
    // 联华
    if (window.location.host.indexOf("os.chinalh.com") >= 0) {
        if (window.location.host.indexOf('testpartner')>=0 || window.location.host.indexOf('testdsp')>=0 || window.location.host.indexOf('testlocal')>=0) {
            hosts = "https://dcg-test-public.obs.cn-east-3.myhuaweicloud.com/"
        } else {
            hosts = "https://dcg-public.obs.cn-east-3.myhuaweicloud.com/"
        }
    }
    return hosts
}

export default {
    deepCopy,
    mix,
    getMapValueByStringKey,
    setMapValueByStringKey,
    isExist,
    isValidateNumber,
    isNotEmptyString,
    hasKey,
    toNumber,
    assert,
    queryUrl,
    createComposeFunc,
    quickSortByKey,
    uniqueString,
    isArray,
    isObject,
    correctNaN,
    toNumberForce,
    toFixed,
    toFixedNumber,
    throttle,
    unique,
    expandArray,
    math,
    debounce,
    listExchange,
    toDouble,
    getDate,
    quickSort,
    arrMixValue,
    arrAddition,
    isEmptyObj,
    getSameItemFromList,
    findListFirstItem,
    numberZoomIn,
    numberZoomOut,
    createDeepReader,
    isJsonString,
    hasValue,
    getTargetValueKeyName,
    findListLastItem,
    listDeleteItems,
    getImgHost
}
